import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";

import {
  ProjectDataForm,
  type ProjectDataFormApi,
} from "@/features/project-data/ProjectDataForm";
import { CompletenessPanel } from "@/features/project-data/CompletenessPanel";
import { DictateToFillPanel } from "@/features/project-data/DictateToFillPanel";
import { useIntakeStore } from "@/features/project-data/useIntakeStore";
import { useExtractionStore } from "@/features/project-data/useExtractionStore";
import {
  toGenerateRequest,
  type ProjectDataInput,
  type ProjectDataValues,
} from "@/features/project-data/schema";
import {
  applyOfficeProfileToDraft,
  officeProfilePatch,
} from "@/features/project-data/prefillToDraft";
import {
  amendDocuments,
  DenialError,
  generateDocuments,
  type DenialDetail,
} from "@/api/client";
import { useDocgenStore } from "@/features/docgen/useDocgenStore";
import { useProjectStore } from "@/features/projects/useProjectStore";
import { officePayload, sprancaRequestValue, useOfficeStore } from "@/features/projects/useOfficeStore";
import { Button } from "@/components/ui/button";
import { PlanMissingBanner } from "@/features/feasibility/PlanMissingBanner";
import { DenialBanner } from "@/features/docgen/DenialBanner";
import { GeneratingProgress } from "@/features/docgen/GeneratingProgress";
import {
  streamingEnabled,
  useStreamingDocgen,
} from "@/hooks/useStreamingDocgen";

/**
 * Korak 2 — PROVJERI. The pre-filled form (hydrated from the draft the
 * Donesi step persisted), an advisory readiness check, and the single
 * generate CTA. This screen owns the whole generation machinery (stream /
 * blocking / incremental amend) and routes to Preuzmi on success.
 *
 * Deliberately NOT here (the ogoljavanje): the feasibility/gradivost check
 * (separate compliance module), the legal-backbone citation picker and the
 * legal verifier (internal QA), the office letterhead (Postavke).
 */
export function ProvjeriPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const setData = useDocgenStore((s) => s.setData);
  const previousDoc = useDocgenStore((s) => s.data);
  const setActive = useProjectStore((s) => s.setActive);
  const project = useProjectStore((s) =>
    s.projects.find((p) => p.id === projectId),
  );
  const upsert = useProjectStore((s) => s.upsert);

  const useStreaming = streamingEnabled();
  const stream = useStreamingDocgen();
  const [submittedValues, setSubmittedValues] =
    useState<ProjectDataValues | null>(null);

  const formApi = useRef<ProjectDataFormApi | null>(null);

  // "Za provjeru" filter — show only low-confidence fields, toggled inline.
  const [reviewFilter, setReviewFilter] = useState(false);
  const lowConfidence = useExtractionStore(
    (s) =>
      Object.values(s.confidence).filter(
        (f) => f && f.value != null && f.value !== "" && (f.confidence ?? 1) < 0.75,
      ).length,
  );

  useEffect(() => {
    if (project) {
      setActive(project.id);
      useExtractionStore.getState().setActiveProject(project.id);
    }
  }, [project, setActive]);

  // Office profile → draft, fill-empty (same hydration Donesi runs on
  // mount). Without it, entering step 2 directly ("Nastavi rad" from the
  // landing) skipped the ured/projektant data set once in Postavke. The
  // form's initialValues get the same patch below — this effect only
  // persists it, since the form has already read the draft by the time
  // effects run.
  const office = useOfficeStore((s) => s.office);
  useEffect(() => {
    if (project?.id) applyOfficeProfileToDraft(project.id, office);
  }, [project?.id, office]);

  // ---- request payload assembly (read at submit time) ---------------------
  const confirmationPayload = () => {
    const current = useProjectStore
      .getState()
      .projects.find((p) => p.id === projectId);
    const lc = current?.legalConfirmation;
    return lc?.citations?.length
      ? { confirmed_at: lc.confirmedAt, citations: lc.citations }
      : null;
  };

  const intakePayload = () => {
    const entry = useIntakeStore.getState().get(projectId);
    if (
      !entry.situacija &&
      entry.recognizedDocs.length === 0 &&
      entry.nacrtVision.length === 0
    )
      return null;
    return {
      situacija: entry.situacija ?? null,
      recognized_docs: entry.recognizedDocs,
      // B1 — vision-over-nacrti. The backend uses opis_prose from each result
      // and only the architect-confirmed facts; empty → byte-identical.
      nacrt_opis: entry.nacrtVision as unknown as Array<
        Record<string, unknown>
      >,
      nacrt_confirmed_facts: entry.nacrtConfirmedFacts as unknown as Array<
        Record<string, unknown>
      >,
    };
  };

  const officeReq = () => officePayload(useOfficeStore.getState().office);
  const sprancaReq = () => sprancaRequestValue();

  const mutation = useMutation({
    mutationFn: (values: ProjectDataValues) =>
      generateDocuments(
        toGenerateRequest(values, confirmationPayload(), intakePayload(), officeReq(), sprancaReq()),
      ),
    onSuccess: (data, vars) => {
      setData(data);
      if (project) {
        upsert({ ...project, projectData: vars, currentStep: 3 });
      }
      navigate(`/project/${projectId}/preuzmi`);
    },
  });

  const amendMutation = useMutation({
    mutationFn: (values: ProjectDataValues) => {
      const prev = useDocgenStore.getState().data;
      if (!prev) throw new Error("Nema prethodnog dokumenta za ažuriranje.");
      return amendDocuments({
        ...toGenerateRequest(values, confirmationPayload(), intakePayload(), officeReq(), sprancaReq()),
        previous_sections: [
          ...prev.izjave,
          ...prev.naslovnice,
          ...prev.tekstualni_dio,
          ...prev.tables,
        ],
        previous_fact_sheet: prev.fact_sheet,
        previous_opci_variables: prev.opci_variables,
      });
    },
    onSuccess: (data, vars) => {
      setData(data);
      if (project) {
        upsert({ ...project, projectData: vars, currentStep: 3 });
      }
      navigate(`/project/${projectId}/preuzmi`);
    },
  });

  const submitModeRef = useRef<"generate" | "amend">("generate");

  const streamDoneHandled = useRef(false);
  useEffect(() => {
    if (stream.status !== "done" || !stream.result || !submittedValues) return;
    if (streamDoneHandled.current) return;
    streamDoneHandled.current = true;
    setData(stream.result);
    const current = useProjectStore
      .getState()
      .projects.find((p) => p.id === projectId);
    if (current) {
      upsert({ ...current, projectData: submittedValues, currentStep: 3 });
    }
    navigate(`/project/${projectId}/preuzmi`);
  }, [
    stream.status,
    stream.result,
    submittedValues,
    upsert,
    setData,
    navigate,
    projectId,
  ]);

  if (!project) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-8 text-center">
        <p className="text-stone-500">Projekt nije pronađen.</p>
        <Button className="mt-3" onClick={() => navigate("/")}>
          Natrag na početak
        </Button>
      </div>
    );
  }

  const submitting =
    mutation.isPending || amendMutation.isPending || stream.status === "streaming";

  const handleSubmit = (values: ProjectDataValues) => {
    if (submitModeRef.current === "amend" && previousDoc) {
      submitModeRef.current = "generate";
      amendMutation.mutate(values);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmittedValues(values);
    streamDoneHandled.current = false;
    if (useStreaming) {
      void stream.start(
        toGenerateRequest(values, confirmationPayload(), intakePayload(), officeReq(), sprancaReq()),
      );
    } else {
      mutation.mutate(values);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDraftChange = (values: Partial<ProjectDataInput>) => {
    const current = useProjectStore
      .getState()
      .projects.find((p) => p.id === projectId);
    if (!current) return;
    upsert({
      ...current,
      projectData: values,
      updatedAt: new Date().toISOString(),
    });
  };

  const denial: DenialDetail | null =
    mutation.error instanceof DenialError
      ? mutation.error.detail
      : stream.status === "error" && stream.denial
        ? stream.denial
        : null;

  const errorMessage = denial
    ? null
    : mutation.error instanceof Error
      ? mutation.error.message
      : stream.status === "error"
        ? stream.error
        : null;

  const showProgress =
    useStreaming &&
    (stream.status === "streaming" ||
      stream.status === "done" ||
      stream.status === "error");

  const readiness = useIntakeStore(
    (s) => s.get(projectId).opciDioStatus?.summary?.readiness ?? null,
  );

  const opciDioParams = {
    building_type: project.feasibility?.building_type ?? "obiteljska_kuca",
    building_subtype: project.feasibility?.building_subtype || null,
    context: project.feasibility?.context || null,
    work_type: project.feasibility?.work_type ?? "novogradnja",
  };

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[1.05rem] font-bold text-stone-800">
            Provjeri podatke — {project.name}
          </h2>
          <p className="text-[0.88rem] text-stone-500">
            Pregledaj što je sustav pročitao iz nacrta i dokumenata; ispravi
            žute (manje pouzdane) prije generiranja.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/project/${project.id}/donesi`)}
        >
          <ArrowLeft className="h-4 w-4" />
          Natrag na Donesi
        </Button>
      </div>

      {denial && <DenialBanner denial={denial} />}

      {errorMessage && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700">
          Greška: {errorMessage}
        </div>
      )}

      {showProgress && (
        <div className="mb-6">
          <GeneratingProgress state={stream} />
        </div>
      )}

      {/* Single "turn the filter ON" control — shown only while the filter
          is off. Turning it OFF is handled by the form's own "Prikaži sva
          polja" banner, so there's never a confusing pair of buttons. */}
      {lowConfidence > 0 && !reviewFilter && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[0.85rem] text-amber-900">
          <span>
            {lowConfidence} {lowConfidence === 1 ? "polje ima" : "polja imaju"}{" "}
            pouzdanost ispod 75 % — vrijedi ih provjeriti.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setReviewFilter(true);
              window.setTimeout(() => {
                document
                  .getElementById("provjeri-forma")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 30);
            }}
          >
            Prikaži samo za provjeru
          </Button>
        </div>
      )}

      {/* R1 — plan-mismatch guard: warn up front when no spatial plan can
          be keyed for this project (unknown municipality + unresolvable
          k.o.), before the architect generates documents. */}
      <PlanMissingBanner
        municipalityCode={
          project.projectData?.municipality_code ??
          project.feasibility?.municipality_code
        }
        katastarskaOpcina={project.projectData?.katastarski_opcina}
        projectId={project.id}
      />

      {/* Dictate/type values straight into the form while reviewing —
          fills empties, refines soft values, leaves measured DXF/IFC
          values untouched (the merge layer enforces that). */}
      <DictateToFillPanel
        onExtracted={(extracted, warnings) =>
          formApi.current?.applyNlExtraction(extracted, warnings)
        }
      />

      <div id="provjeri-forma" className="scroll-mt-24">
        <ProjectDataForm
          initialValues={{
            ...project.projectData,
            // Office profile fill-empty, mixed in directly: the form reads
            // the draft once at mount, before the hydration effect above
            // has persisted anything.
            ...(officeProfilePatch(
              (project.projectData ?? {}) as Record<string, unknown>,
              office,
            ) as Partial<ProjectDataInput>),
            // "" = municipality unknown — backend resolves k.o.→JLS or
            // renders the plan-missing placeholder (R1). Never default to
            // a concrete municipality here.
            municipality_code:
              project.projectData?.municipality_code ??
              project.feasibility?.municipality_code ??
              "",
          }}
          onSubmit={handleSubmit}
          onDraftChange={handleDraftChange}
          submitting={submitting}
          formId="project-data-form"
          externalSubmit
          apiRef={formApi}
          reviewFilter={reviewFilter}
          onClearReviewFilter={() => setReviewFilter(false)}
        />
      </div>

      <div className="mt-2">
        <CompletenessPanel projectId={project.id} params={opciDioParams} />
      </div>

      {/* Sticky action bar — the primary CTA: generate. */}
      <div className="sticky bottom-0 z-30 -mx-6 mt-6 border-t border-stone-200 bg-white/95 px-6 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <span className="text-[0.78rem] text-stone-400">
            Unosi se automatski spremaju u nacrt projekta.
          </span>
          <div className="flex items-center gap-3">
            {readiness && !readiness.ready && (
              <span className="text-[0.78rem] font-medium text-amber-700">
                ⚠ {readiness.blockers.length}{" "}
                {readiness.blockers.length === 1
                  ? "stavka nedostaje"
                  : "stavki nedostaje"}
              </span>
            )}
            {readiness?.ready && (
              <span className="text-[0.78rem] font-medium text-green-700">
                ✓ spremno
              </span>
            )}
            {previousDoc && (
              <Button
                type="submit"
                form="project-data-form"
                variant="outline"
                disabled={submitting}
                onClick={() => {
                  submitModeRef.current = "amend";
                }}
                title="Regeneriraj samo sekcije na koje su promjene utjecale; zaključane/ručno uređene ostaju netaknute"
              >
                <RefreshCw className="h-4 w-4" />
                {amendMutation.isPending ? "Ažuriram…" : "Ažuriraj (samo izmjene)"}
              </Button>
            )}
            <Button
              type="submit"
              form="project-data-form"
              disabled={submitting}
              onClick={() => {
                submitModeRef.current = "generate";
              }}
            >
              <Sparkles className="h-4 w-4" />
              {mutation.isPending || stream.status === "streaming"
                ? "Generiram dokumente…"
                : previousDoc
                  ? "Generiraj iznova"
                  : "Generiraj dokumentaciju"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
